import { CategoryApi, CategoryDataType } from "@/api/CategoryApi";
import { ProductApi, ProductDataType } from "@/api/ProductsApi";
import { drain } from "./core/drain";
import {
    DEFAULT_MEASURE_UNIT,
    DEFAULT_TAX_PERCENTAGE_CODE,
    MissingCategory,
    nextAvailableCode,
    normalizeCategoryName,
} from "./resolveProductRows";
import { productTypeFromCabys } from "@utils/Cabys";
import { IVA_TAX_TYPE } from "@utils/Constants";

export type ExistingProductData = {
    products: ProductDataType[];
    categories: CategoryDataType[];
};

const drainProducts = async (): Promise<ProductDataType[]> => {
    const all: ProductDataType[] = [];
    let token: string | undefined = undefined;

    do {
        const { items, nextToken } = await ProductApi.fetchAllForImport(token);
        all.push(...items);
        token = nextToken;
    } while (token);

    return all;
};

/** Trae todos los productos y categorias del tenant. */
export const loadExistingProductData = async (): Promise<ExistingProductData> => {
    const [products, categories] = await Promise.all([drainProducts(), drain(CategoryApi)]);
    return { products, categories };
};

/**
 * Crea las categorias que el archivo menciona y el tenant todavia no tiene.
 *
 * Se hace desde el navegador (son unas pocas, no cientos) para que `CategoryApi` invalide
 * su cache y para que la lambda reciba `categoryId` ya resueltos. El CAByS y los defaults
 * de impuesto salen del primer producto del archivo que cae en la categoria.
 */
export const createMissingCategories = async (
    missingCategories: MissingCategory[],
    existingCategories: CategoryDataType[]
): Promise<CategoryDataType[]> => {
    if (!missingCategories.length) return existingCategories;

    let nextCode = nextAvailableCode(existingCategories);
    const created: CategoryDataType[] = [];

    for (const missing of missingCategories) {
        const category = await CategoryApi.create({
            nombre: missing.nombre.trim(),
            codigo: `${nextCode}`,
            codigo_cabys: missing.codigo_cabys,
            tipo_producto: missing.tipo_producto ?? productTypeFromCabys(missing.codigo_cabys),
            tipo_impuesto: missing.tipo_impuesto || IVA_TAX_TYPE,
            porcentaje_impuesto: missing.porcentaje_impuesto || DEFAULT_TAX_PERCENTAGE_CODE,
            unidad_medida: missing.unidad_medida || DEFAULT_MEASURE_UNIT,
        });
        nextCode += 1;
        created.push(category);
    }

    return [...existingCategories, ...created];
};

/** Categorias indexadas por nombre normalizado, para resolver `categoryId` tras crearlas. */
export const indexCategoriesByName = (categories: CategoryDataType[]): Map<string, CategoryDataType> => {
    const index = new Map<string, CategoryDataType>();
    categories.forEach((category) => {
        const key = normalizeCategoryName(category.nombre);
        if (key && category.id && !index.has(key)) index.set(key, category);
    });
    return index;
};
